/* eslint-disable react/jsx-props-no-spreading, react/no-array-index-key */
import { useWindowSize } from '@folklore/hooks';
import { DataProvider } from '@micromag/data';
import '@micromag/intl/locale/fr';
import Viewer from '@micromag/viewer';
import classNames from 'classnames';
import React, { useState, useCallback, useEffect } from 'react';

import styles from '../../styles/partials/micromag.module.css';

import '@micromag/consent/assets/css/styles.css';
import '@micromag/core/assets/css/styles.css';
import '@micromag/viewer/assets/css/styles.css';

interface MicromagProps {
    micromag?: Record<string, any> | null;
    paused?: boolean;
    fullscreen?: boolean;
    autoFullscreen?: boolean;
    withoutPlaybackControls?: boolean;
    onClose?: (() => void) | null;
    onEnd?: (() => void) | null;
    onMenuChange?: (() => void) | null;
    className?: string | null;
    [key: string]: any;
}

function Micromag({
    micromag = {},
    paused = false,
    fullscreen: initialFullscreen = false,
    autoFullscreen = false,
    withoutPlaybackControls = false,
    className = null,
    onClose: customOnClose = null,
    onEnd: customOnEnd = null,
    onMenuChange = null,
    ...props
}: MicromagProps) {
    const { locale = 'fr' } = micromag || {};
    const googleApiKey = null;
    const micromagApiBaseUrl = 'https://microm.ag/api';
    const { organisation = null } = micromag || {};
    const { branding = null } = organisation || {};
    const disableScroll = false;
    const { width = 0, height = 0 } = useWindowSize();
    const [landscape, setLandscape] = useState(width > height);
    const [fullscreen, setFullscreen] = useState(autoFullscreen && !landscape && initialFullscreen);

    const onClose = useCallback(() => {
        if (autoFullscreen && fullscreen) {
            setFullscreen(false);
        }
        if (customOnClose !== null) {
            customOnClose();
        }
    }, [autoFullscreen, fullscreen, setFullscreen, customOnClose]);

    const onEnd = useCallback(() => {
        if (autoFullscreen && fullscreen) {
            setFullscreen(false);
        }
        if (customOnEnd !== null) {
            customOnEnd();
        }
    }, [autoFullscreen, fullscreen, setFullscreen, customOnEnd]);

    const onViewModeChange = useCallback(
        ({ landscape: isLandscape = false }: { landscape?: boolean }) => {
            setLandscape(isLandscape);
            if (autoFullscreen && !isLandscape) {
                setFullscreen(true);
            }
        },
        [autoFullscreen, setLandscape, setFullscreen],
    );

    useEffect(() => {
        if (autoFullscreen) {
            (disableScroll as any)(fullscreen);
        }
    }, [autoFullscreen, fullscreen]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.fullscreen]: fullscreen,
                    [className as string]: className !== null,
                },
            ])}
        >
            <DataProvider apiBaseUrl={micromagApiBaseUrl}>
                <Viewer
                    story={micromag}
                    paused={paused}
                    theme={branding}
                    locale={locale}
                    visitor={null}
                    pathWithIndex
                    closeable={(autoFullscreen && fullscreen) || customOnClose !== null}
                    googleApiKey={googleApiKey}
                    onViewModeChange={onViewModeChange}
                    onMenuChange={onMenuChange}
                    onClose={onClose}
                    onEnd={onEnd}
                    withoutPlaybackControls={withoutPlaybackControls}
                    withNavigationHint
                    {...props}
                />
            </DataProvider>
        </div>
    );
}

export default React.memo(Micromag);
